import { KeyRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useInstallations } from '../installations/api'
import { fmtDate } from '../shared/format'
import {
  useCreateLicenseRequest,
  useDownloadLicenseEnvelope,
  useExportLicenseRequests,
  useInstallationLicenses,
} from './api'

export function LicensesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const installations = useInstallations()
  const [installationId, setInstallationId] = useState('')
  const list = useInstallationLicenses(installationId)
  const createReq = useCreateLicenseRequest()
  const exportReq = useExportLicenseRequests()
  const envelope = useDownloadLicenseEnvelope()
  const [reqOpen, setReqOpen] = useState(false)
  const [reason, setReason] = useState('')

  const fail = (e: unknown) => toast.error(apiErrorMessage(e))
  const issue = can('licenses.issue')

  const { pageRows, pager } = usePagination(list.data ?? [], installationId)
  return (
    <>
      <PageHeader
        title={t('licenses.title')}
        description={t('licenses.subtitle')}
        actions={
          issue ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  void exportReq
                    .mutateAsync()
                    .then(() => toast.success(t('licenses.exportDone')))
                    .catch(fail)
                }
              >
                {t('licenses.export')}
              </Button>
              <Button disabled={!installationId} onClick={() => setReqOpen(true)}>
                {t('licenses.newRequest')}
              </Button>
            </div>
          ) : null
        }
      />

      <Card className="mb-4 p-4">
        <div className="space-y-2">
          <Label htmlFor="lic-installation">{t('licenses.selectInstallation')}</Label>
          <Select
            id="lic-installation"
            className="max-w-md"
            placeholder={t('licenses.selectHint')}
            value={installationId}
            onChange={setInstallationId}
            options={(installations.data ?? []).map((i) => ({
              value: i.id,
              label: i.name,
              description: i.account_title ?? i.account_id,
              keywords: [i.account_title ?? i.account_id],
            }))}
          />
        </div>
      </Card>

      <Card>
        {!installationId ? (
          <EmptyState title={t('licenses.selectHint')} />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.isLoading ? (
          <div className="p-4">
            <Skeleton className="h-40" />
          </div>
        ) : (list.data?.length ?? 0) === 0 ? (
          <EmptyState />
        ) : (
          <>
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>{t('licenses.columns.sequence')}</TH>
                  <TH>{t('licenses.columns.status')}</TH>
                  <TH>{t('licenses.columns.reason')}</TH>
                  <TH className="text-right">{t('licenses.columns.created')}</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {pageRows.map((l) => (
                  <TR key={l.license_id}>
                    <TD className="font-medium">
                      #{l.sequence}
                      {l.stale ? <Badge className="ml-2" tone="warning">{t('licenses.stale')}</Badge> : null}
                    </TD>
                    <TD>
                      <Badge>{l.status}</Badge>
                    </TD>
                    <TD className="text-muted-foreground">{l.reason ?? '—'}</TD>
                    <TD className="text-right text-muted-foreground">{fmtDate(l.issued_at ?? l.created_at)}</TD>
                    <TD className="text-right">
                      {issue && l.status !== 'pending_signature' && l.status !== 'cancelled' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            void envelope
                              .mutateAsync({ installationId, licenseId: l.license_id, sequence: l.sequence })
                              .then(() => toast.success(t('licenses.envelopeDone')))
                              .catch(fail)
                          }
                        >
                          {t('licenses.envelope')}
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/installations/${installationId}`}>{t('licenses.installation')}</Link>
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pager {...pager} />
          </>
        )}
      </Card>

      <Modal
        open={reqOpen}
        onOpenChange={setReqOpen}
        icon={<KeyRound />}
        title={t('licenses.newRequest')}
        description={t('licenses.requestHint')}
        onSubmit={(e) => {
          e.preventDefault()
          if (!installationId || !reason.trim()) return
          void createReq
            .mutateAsync({ installationId, reason: reason.trim() })
            .then(() => {
              toast.success(t('common.saved'))
              setReason('')
              setReqOpen(false)
            })
            .catch(fail)
        }}
        footer={
          <Button type="submit" disabled={createReq.isPending}>
            {t('common.save')}
          </Button>
        }
      >
        <div className="space-y-1">
          <Label htmlFor="lic-reason">{t('licenses.reason')}</Label>
          <Input id="lic-reason" value={reason} onChange={(e) => setReason(e.target.value)} required />
        </div>
      </Modal>
    </>
  )
}
